import { config } from '@/configs'
import { ErrorScreen } from '@/domains/appShell/error'
import { Loading } from '@/domains/appShell/loading'
import {
  Provider,
  msalAtom,
  sessionAtom,
  sessionProviderAtom,
} from '@/domains/appShell/stores/auth.store'

import { type PropsWithChildren, useEffect, useState } from 'react'
import { BrowserRouter } from 'react-router-dom'

import { MsalProvider } from '@azure/msal-react'
import * as Sentry from '@sentry/react'
import { useAtom, useAtomValue } from 'jotai'

const getStoredSessionProvider = (): Provider | null => {
  try {
    const stored = localStorage.getItem('sessionProvider')
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}

const isMsalLoginRedirect = () => /[?&#](code|error)=/.test(window.location.href)

/**
 * Provider d'authentification Azure MSAL.
 *
 * Initialise MSAL, traite les callbacks de redirection, acquiert les tokens
 * en mode silencieux et maintient l'état de session global.
 *
 * @param children - Composants enfants à rendre avec le contexte d'auth
 * @returns JSX avec provider MSAL ou vue d'erreur
 */
export const AuthProviders = ({ children }: PropsWithChildren) => {
  const msal = useAtomValue(msalAtom)
  const sessionProvider = useAtomValue(sessionProviderAtom)

  const [session, setSession] = useAtom(sessionAtom)

  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(() => {
    if (session) return false

    const pendingProvider = sessionProvider ?? getStoredSessionProvider()

    if (pendingProvider === Provider.MSAL) return isMsalLoginRedirect()

    return false
  })

  // Adjusted during render rather than in an effect, to skip the extra render pass.
  if (isLoading && session) {
    setIsLoading(false)
  }

  /**
   * MSAL
   */
  useEffect(() => {
    void (async () => {
      try {
        await msal.initialize()

        if (!session && sessionProvider === Provider.MSAL) {
          // Attendre que MSAL termine le traitement du callback de redirection
          const response = await msal.handleRedirectPromise()

          const account = response?.account || msal.getAllAccounts()?.[0]

          if (account) {
            const accessTokenResponse = await msal.acquireTokenSilent({
              scopes: config.auth.entra.scopes,
              account,
            })

            setSession({
              accessToken: accessTokenResponse.accessToken,
              idToken: account.idToken ?? '',
              expiresAt: accessTokenResponse.expiresOn?.getTime() ?? 0,
              provider: Provider.MSAL,
            })
          }
        }
      } catch (error) {
        // TODO use logger
        Sentry.captureException(error, {
          tags: {
            feature: 'authentication',
            provider: 'msal',
            operation: 'token-acquisition',
          },
          contexts: {
            auth: {
              sessionProvider,
              hasExistingSession: !!session,
              accountsCount: msal?.getAllAccounts()?.length ?? 0,
            },
          },
          level: 'error',
        })
        setError('Failed to load MSAL')
      } finally {
        if (sessionProvider === Provider.MSAL) {
          setIsLoading(false)
        }
      }
    })()
  }, [session, msal, sessionProvider, setSession])

  // The error screen is rendered INSTEAD of `children`, so the app's only Router — the
  // `RouterProvider` inside `Main` — is not mounted on this path. The screen links back to the home
  // page, and react-router throws when a `Link` renders outside a Router context, so an auth
  // failure used to blank the page instead of showing this screen. A local `BrowserRouter` gives it
  // the context it needs. The screen is also imported eagerly rather than lazily: nothing wraps it
  // in a `Suspense` boundary here, and a chunk fetch is a poor bet on an already degraded boot.
  //
  // This makes the screen VISIBLE, not functional: its home link only pushes a history entry, and
  // `error` stays true in the state above, so the same screen re-renders. The screen also still
  // reads "404 / page not found", which is wrong for an auth failure. Both are pre-existing and
  // out of scope here — see .docs/archive/conception/migration-bo-ds-error/plan.md, arbitrage #5.
  if (error)
    return (
      <BrowserRouter>
        <ErrorScreen />
      </BrowserRouter>
    )
  if (isLoading && !session) return <Loading />

  return <MsalProvider instance={msal}>{children}</MsalProvider>
}
